import { Head, router } from "@inertiajs/react"
import { Bot, Star, User } from "lucide-react"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import PartnerLayout from "@/layouts/partner-layout"
import { partnerAccountPath } from "@/routes"

interface SearchResult {
  id: string
  record_type: "Account" | "Agent" | "User"
  name: string
  email: string | null
  account_name: string | null
  account_uid?: string | null
}

const typeIcons = { Account: Star, Agent: Bot, User: User } as const

export default function SearchResults({
  query,
  results,
}: {
  query: string
  results: SearchResult[]
}) {
  function handleClick(result: SearchResult) {
    if (result.record_type === "Account") {
      router.visit(partnerAccountPath({ id: result.id }))
    } else if (result.record_type === "Agent" && result.account_uid) {
      // IMP-511: the Agents page is retired — the agent's home is its
      // account's Billing tab (the jobs list).
      router.visit(
        `${partnerAccountPath({ id: result.account_uid })}?tab=billing`,
      )
    }
  }

  return (
    <PartnerLayout>
      <Head title={`Search: ${query}`} />
      <div className="flex flex-col gap-4 p-6">
        <h1 className="text-2xl font-semibold">
          Search results for &ldquo;{query}&rdquo;
        </h1>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((result) => {
              const Icon = typeIcons[result.record_type] ?? Star
              return (
                <TableRow
                  key={`${result.record_type}-${result.id}`}
                  className="cursor-pointer"
                  onClick={() => handleClick(result)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Icon className="text-muted-foreground size-4" />
                      <span className="text-muted-foreground text-xs">
                        {result.record_type}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{result.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {result.email ?? result.account_name ?? "-"}
                  </TableCell>
                </TableRow>
              )
            })}
            {results.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-muted-foreground text-center"
                >
                  No results found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </PartnerLayout>
  )
}
